import React, { PropsWithChildren, ReactElement } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import { Colour, ColourPalette, ColourWeight } from "../../theme/theme";
import { isTextLight } from "../../theme/theme.util";
import { Interpolation } from "@emotion/serialize/dist/emotion-serialize.cjs";
import Loading from "@components/loading/Loading";
import { SmallLoading } from "@components/loading/SmallLoading";

export type ButtonSize = "sm" | "md" | "lg" | "xl" | "xxl";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "link";

export type ButtonType = "button" | "submit" | "reset";

const horizontalPadding = {
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  xxl: 28,
};

const verticalPadding = {
  sm: 8.5,
  md: 10.5,
  lg: 11.25,
  xl: 13.25,
  xxl: 18,
};

export interface ButtonProps extends PropsWithChildren {
  text?: string;
  size: ButtonSize;
  leadingIcon?: ReactElement;
  trailingIcon?: ReactElement;
  variant?: ButtonVariant;
  color?: Colour;
  grey?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  extraCss?: Interpolation<Theme>;
  fullWidth?: boolean;
  iconButton?: boolean;
  type?: ButtonType;
  loading?: boolean;
}

const Button: React.FC<ButtonProps> = (props) => {
  const color: Colour = props.color || "primary";
  const variant: ButtonVariant = props.variant || "primary";
  const theme = useTheme();
  const disabled = props.disabled || props.loading;

  let fontSize = theme.fonts.size.text.sm;
  if (props.size === "lg" || props.size === "xl") {
    fontSize = theme.fonts.size.text.md;
  } else if (props.size === "xxl") {
    fontSize = theme.fonts.size.text.lg;
  }

  let backgroundColor: ColourPalette = theme.colours[color];
  if (variant !== "primary" && props.grey) {
    backgroundColor = theme.colours.grey;
  }

  let baseWeight: ColourWeight = 600;
  if (variant === "secondary" && props.grey) {
    baseWeight = 0;
  } else if (variant === "secondary") {
    baseWeight = 50;
  } else if (variant === "tertiary" || variant === "link") {
    baseWeight = 0;
  }

  let hoverWeight: ColourWeight = 700;
  if (variant === "secondary" && props.grey) {
    hoverWeight = 50;
  } else if (variant === "secondary") {
    hoverWeight = 100;
  } else if (variant === "tertiary") {
    hoverWeight = 50;
  } else if (variant === "link") {
    hoverWeight = 0;
  }

  let disabledWeight: ColourWeight = 200;
  if (variant === "secondary" && props.grey) {
    disabledWeight = 0;
  } else if (variant === "secondary") {
    disabledWeight = 25;
  } else if (variant === "tertiary" || variant === "link") {
    disabledWeight = 0;
  }

  const textColorWeight: ColourWeight = disabled ? 300 : 700;
  const lightText = isTextLight(backgroundColor[baseWeight]);
  let textColor = lightText ? theme.baseColours.white : theme.baseColours.black;
  if (props.grey) {
    textColor = disabled ? theme.colours.grey[300] : theme.colours.grey[700];
  } else if (variant !== "primary") {
    textColor = theme.colours[color][textColorWeight];
  }

  const leadingIcon = props.leadingIcon
    ? React.cloneElement(props.leadingIcon, {
        width: props.size === "xxl" ? 24 : 20,
        height: props.size === "xxl" ? 24 : 20,
        color: textColor,
        style: {
          margin: "-1.5px 0",
        },
      })
    : null;

  const trailingIcon = props.trailingIcon
    ? React.cloneElement(props.trailingIcon, {
        width: props.size === "xxl" ? 24 : 20,
        height: props.size === "xxl" ? 24 : 20,
        color: textColor,
        style: {
          margin: "-1.5px 0",
        },
      })
    : null;

  const styles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: props.size === "xxl" ? 16 : 8,
    fontFamily: theme.fonts.family.body,
    fontWeight: theme.fonts.weight.semibold,
    fontSize,
    width: props.fullWidth ? "100%" : "fit-content",
    padding:
      variant === "link"
        ? 0
        : `${verticalPadding[props.size]}px ${
            props.iconButton
              ? verticalPadding[props.size]
              : horizontalPadding[props.size]
          }px`,
    borderRadius: 8,
    outline: "none",
    backgroundColor: backgroundColor[baseWeight],
    boxShadow:
      variant === "primary" || variant === "secondary"
        ? theme.shadows.xs
        : "none",
    cursor: "pointer",
    border:
      variant === "secondary" && props.grey
        ? `1px solid ${theme.colours.grey[300]}`
        : `1px solid ${backgroundColor[baseWeight]}`,
    color: props.loading ? "transparent" : textColor,
    "&:hover": {
      backgroundColor: backgroundColor[hoverWeight],
      border:
        variant === "secondary" && props.grey
          ? `1px solid ${theme.colours.grey[300]}`
          : `1px solid ${backgroundColor[hoverWeight]}`,
    },
    "&:active": {
      backgroundColor: backgroundColor[baseWeight],
      border:
        variant === "secondary" && props.grey
          ? `1px solid ${theme.colours.grey[300]}`
          : `1px solid ${backgroundColor[hoverWeight]}`,
      color: !disabled && variant === "link" ? backgroundColor[800] : undefined,
    },
    "&:disabled": {
      cursor: "default",
      backgroundColor: backgroundColor[disabledWeight],
      border:
        variant === "secondary" && props.grey
          ? `1px solid ${theme.colours.grey[200]}`
          : `1px solid ${backgroundColor[disabledWeight]}`,
    },
    "&:focus": {
      boxShadow:
        variant === "tertiary" || variant === "link"
          ? "none"
          : theme.shadows.focus(backgroundColor[400]),
    },
    transition: "all 0.15s ease-in-out",
  });

  return (
    <button
      type={props.type}
      onClick={props.onClick}
      disabled={disabled}
      css={[styles, props.extraCss]}
    >
      {leadingIcon}
      {props.text}
      {props.children ? props.children : null}
      {props.loading && <SmallLoading color={lightText ? "light" : "dark"} />}
      {trailingIcon}
    </button>
  );
};

export default Button;
